import { gsap } from 'gsap';
import { readGlobalState } from '../util/helpers';
import { mq } from '../util/matchMedia';

// GLOSSARY:
// -> Studio: the section of the site
// -> Collection: each set of 4 media items within the Studio section
// -> Carousel: the automated transition between Collections

export const studioCarousel = (() => {
  const INTERVAL = 3; // Delay between slides
  const SLIDE_DURATION = 4; // Duration of entire slide animation
  const FADE_DURATION = 1; // Duration of each media item fade
  const ANIMATION_ORDER_DESKTOP = [1, 3, 2, 0]; // Order of images (clockwise)
  const ANIMATION_ORDER_MOBILE = [1, 2, 3, 0]; 

  let animationOrder, carouselTimeline, collections, allMediaItems;

  const init = (delay) => {
    getReferenceElements();
    createCarouselTimeline(delay);
  }

  const getReferenceElements = () => {
    allMediaItems = document.querySelectorAll('.js-studio-gallery-media');
    collections = document.querySelectorAll(`.js-studio-gallery-${readGlobalState('mq')} .js-studio-gallery-collection`);
  }

  const createCarouselTimeline = (delay) => {
    animationOrder = mq.isDesktop ? ANIMATION_ORDER_DESKTOP : ANIMATION_ORDER_MOBILE;
    // If there's only one collection, don't repeat.
    const repeatAmount = collections.length < 2 ? 0 : -1;

    carouselTimeline = gsap.timeline({
      paused: true,
      repeat: repeatAmount,
      delay: delay,
      repeatDelay: INTERVAL, // Delay before it loops again
      onStart: () => {
        // Collections hidden by default until first slide has animated
        gsap.to(collections, {
          autoAlpha: 1,
          delay: FADE_DURATION + SLIDE_DURATION,
        })
      },
    });

    collections.forEach((collection, index) => {
      const delay = index ? INTERVAL : 0;
      carouselTimeline.add(animateCollection(collection, index), `+=${delay}`);
    });

  }

  const animateCollection = (collection) => {
    if (!collection) return;

    const media = collection.querySelectorAll('.js-studio-gallery-media');

    const collectionTimeline = gsap.timeline({
      onComplete: playVideo,
      onCompleteParams: [ collection ],
    });

    collectionTimeline
      .set(allMediaItems, { pointerEvents: 'none' })

      .set(collection, {
        autoAlpha: 1,
        zIndex: 2,
      })

      .set(media, { autoAlpha: 0 })

      .to(media, {
        autoAlpha: 1,
        duration: FADE_DURATION,
        ease: 'Power1.inOut',
        stagger: function (index) {
          const factor = animationOrder.indexOf(index) + 1;
          return factor * (SLIDE_DURATION / 6);
        },
      })

      .set(media, { pointerEvents: 'auto' })

      .set(collection, { zIndex: 1 })
    
    return collectionTimeline;
  }

  const playVideo = (collection) => {
    const video = collection.querySelector('.js-studio-gallery-media-video');
    const poster = collection.querySelector('.js-studio-gallery-media-poster');
    if (!video || !poster) return;

    carouselTimeline.pause();

    const play = video.play.bind(video);

    const tl = gsap.timeline({ delay: 1.25 });
      tl
        .to(poster, { autoAlpha: 0, duration: 1 })
        .call(play, null, '>-1')
    
    video.addEventListener('ended', () => {
      gsap.to(poster, { autoAlpha: 1, duration: 1 });
      // Skip forward 1.5 seconds, to switch slides sooner
      carouselTimeline.play(carouselTimeline.time() + 1.5);
    });

    video.play();
  }


  const play = () => carouselTimeline.play();
  const pause = () => carouselTimeline.pause();
  const resume = () => carouselTimeline.resume();

  return { init, play, pause, resume }
})();